import React, { useState, useMemo } from 'react';
import { getFormattedNumber } from '@ecg-internal/aurora-js-formatters';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  RowHeader,
  BasicModalWithFooter,
  FieldHub,
  TextLarge,
  UtilsBlock,
  CheckboxSelectionGroup,
  PrimaryButton,
} from '@hz-design-system/web-ui';
import { TIcon } from '@hz-design-system/web-ui/lib/types/TStyleVariables.ts';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import LinkGenerator from '@/client/components/LinkGenerator';
import { getRecentL2Categories } from '@/client/actions/storage/recentL2Categories';

const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface MultiCategoryFacetProps {
  categories: TFacetCategory[];
  inputKey: string;
  label: string;
  searchRequestObject: TSearchRequestObject;
}

type FormattedCategory = TFacetCategory & {
  items: TFacetCategory[];
  selected: boolean;
};

const getFormattedCategories = (categories: TFacetCategory[]): FormattedCategory[] => {
  if (!Array.isArray(categories)) return [];

  // First pass: add all L1 categories (no parentId)
  const l1Categories: FormattedCategory[] = categories
    .filter((item) => !item.parentId)
    .map((item) => ({
      ...item,
      items: [],
      selected: item.selected,
      histogramCount: 0,
    }));

  // Second pass: add L2 categories to their parents
  categories
    .filter((item) => item.parentId)
    .forEach((item) => {
      const parentCategory = l1Categories.find((cat) => cat.id === item.parentId);
      if (parentCategory) {
        parentCategory.items.push(item);
        parentCategory.histogramCount = (parentCategory.histogramCount || 0) + (item.histogramCount || 0);
      }
    });

  return l1Categories;
};

const getL1Description = (
  t: (key: string) => string,
  tExists: (key: string) => boolean,
  l1CategoryKey: string,
  selectedL2Items: TFacetCategory[],
) => {
  if (selectedL2Items.length > 0) {
    return selectedL2Items.map((item) => item.label).join(', ');
  }
  return tExists(`facets.category.all.${l1CategoryKey}`) ? t(`facets.category.all.${l1CategoryKey}`) : t(`facets.all`);
};

const MultiCategoryFacet: React.FC<MultiCategoryFacetProps> = ({
  categories,
  inputKey,
  label,
  searchRequestObject,
}) => {
  const { t, tExists } = useI18nContext();
  const formattedCategories = getFormattedCategories(categories);
  const [modalL1Category, setModalL1Category] = useState<FormattedCategory | null>(null);

  const recentL2Ids = useMemo(() => {
    if (!modalL1Category) return new Set<number>();
    return new Set(getRecentL2Categories(modalL1Category.id));
  }, [modalL1Category]);

  const selectedValues = useMemo(() => {
    if (!modalL1Category) return [];
    return modalL1Category.items.filter((item) => item.selected).map((item) => item.id.toString());
  }, [modalL1Category]);

  const recentL2Options = useMemo(() => {
    if (!modalL1Category) return [];
    const selectedItems = modalL1Category.items.filter((item) => item.selected);
    if (selectedItems.length >= 2) return selectedItems;
    const selectedIds = new Set(selectedItems.map((item) => item.id));
    const storedItems = modalL1Category.items.filter(
      (item) => !item.selected && recentL2Ids.has(item.id) && !selectedIds.has(item.id),
    );
    const slotsAvailable = 2 - selectedItems.length;
    return [...selectedItems, ...storedItems.slice(0, slotsAvailable)];
  }, [modalL1Category, recentL2Ids]);

  const toCheckboxOption = (item: TFacetCategory) => ({
    value: item.id.toString(),
    labelText: item.label || '',
    counter:
      typeof item.histogramCount === 'number' && item.histogramCount >= 0
        ? getFormattedNumber(item.histogramCount)
        : undefined,
  });

  const handleSelectionChange = (values: string[]) => {
    setModalL1Category((prev) => {
      if (!prev) return prev;
      const selectedIds = new Set(values);
      return {
        ...prev,
        items: prev.items.map((item) => ({ ...item, selected: selectedIds.has(item.id.toString()) })),
      };
    });
  };

  const getSelectedL2Categories = (): TCategory[] => {
    if (!modalL1Category) return [];
    return modalL1Category.items
      .filter((item) => item.selected)
      .map((item) => ({ key: item.key, id: item.id }) as TCategory);
  };

  const onCloseModal = () => {
    setModalL1Category(null);
  };

  return (
    <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter' })}>
      <div className="category-row-header">
        <RowHeader title={t(`facets.${inputKey}`, label)} />
      </div>
      {formattedCategories.map((l1Category) => {
        const selectedL2Items = l1Category.items.filter((item) => item.selected);
        return (
          <div key={l1Category.key} id={l1Category.id.toString()}>
            <FieldHub
              bodyTitle={l1Category.label}
              bodyDescription={getL1Description(t, tExists, l1Category.key, selectedL2Items)}
              iconName={l1Category.id.toString() as TIcon}
              isParent
              onClick={() => setModalL1Category(l1Category)}
            />
            <div style={visuallyHiddenStyle} aria-hidden="true">
              {l1Category.items.map((l2Item) => (
                <LinkGenerator
                  key={`seo-${l2Item.key}`}
                  searchRequestObject={searchRequestObject}
                  categories={{
                    l1Category: { key: l1Category.key, id: l1Category.id },
                    l2Categories: [{ key: l2Item.key, id: l2Item.id }],
                  }}
                  tabIndex={-1}
                >
                  {l2Item.label}
                </LinkGenerator>
              ))}
            </div>
          </div>
        );
      })}
      {modalL1Category && (
        <BasicModalWithFooter
          size="s"
          title={modalL1Category.label}
          className="GroupMultiSelectFacetModal"
          onClose={() => onCloseModal()}
          isBottomSheet
          buttonsAlignmentMobile="stretched"
          buttonsAlignmentDesktop="stacked"
          buttons={[
            {
              element: (
                <LinkGenerator
                  searchRequestObject={searchRequestObject}
                  categories={{
                    l1Category: { key: modalL1Category.key, id: modalL1Category.id },
                    l2Categories: getSelectedL2Categories(),
                  }}
                  onClick={() => onCloseModal()}
                >
                  <PrimaryButton>{t('facets.show')}</PrimaryButton>
                </LinkGenerator>
              ),
            },
          ]}
        >
          <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter' })}>
            {recentL2Options.length > 0 && (
              <UtilsBlock mb="m">
                <TextLarge utilities={['marginBottomXxs']} strong>
                  {t('facets.category.recent')}
                </TextLarge>
                <CheckboxSelectionGroup
                  options={recentL2Options.map(toCheckboxOption)}
                  values={selectedValues}
                  onChange={handleSelectionChange}
                />
              </UtilsBlock>
            )}

            <UtilsBlock mb="m">
              <TextLarge utilities={['marginBottomXxs']} strong>
                {t(`facets.category.all.${modalL1Category.key}`)}
              </TextLarge>
              <CheckboxSelectionGroup
                options={modalL1Category.items.map(toCheckboxOption)}
                values={selectedValues}
                onChange={handleSelectionChange}
              />
            </UtilsBlock>
          </div>
        </BasicModalWithFooter>
      )}
    </div>
  );
};

export default MultiCategoryFacet;
